// PreauthorizationVolume.jsx
import React, { useMemo } from "react";
import { Card, Col } from "react-bootstrap";
import ApexCharts from "react-apexcharts";
import { useSelector } from "react-redux";

/**
 * Canonical status config.
 * keys here are the canonical keys we use internally (lowercase, with spaces or dashes).
 */
const statusConfig = {
  approved: { color: "#2c7ce6", bg: "#eef5ff", label: "Approved" },
  rejected: { color: "#ed5e0b", bg: "#fff2e9", label: "Rejected" },
  "no response": { color: "#26bcfc", bg: "#eaf8ff", label: "No Response" },
  "partially approved": {
    color: "#0da9bd",
    bg: "#e6f9fc",
    label: "Partially Approved",
  },
  pended: { color: "#d16e34", bg: "#fff1e9", label: "Pended" },
  prepared: { color: "#5a7184", bg: "#f2f9fc", label: "Prepared" },
  cancelled: { color: "#269dd0", bg: "#e9f8ff", label: "Cancelled" },
  "not-required": { color: "#197aa4", bg: "#e7f6fc", label: "Not Required" },
  queued: { color: "#2a91ed", bg: "#eaf4ff", label: "Queued" },
  error: { color: "#f5803e", bg: "#fff4ef", label: "Error" },
  invalid: { color: "#f5803e", bg: "#fff4ef", label: "Invalid" },
};

/**
 * SAMPLE_DATA — optional fallback for local testing.
 * Remove or comment out once your Redux state supplies the data.
 */
const SAMPLE_DATA = [
  {
    payor_name: "Tawuniya",
    tot_status: {
      "Partially Approved": 16,
      "No Response": 1,
      "not-required": 13,
      approved: 20,
      rejected: 7,
      total: 57,
    },
  },
  {
    payor_name: "Bupa",
    tot_status: {
      approved: 34,
      "not-required": 4,
      rejected: 12,
      "Partially Approved": 2,
      Error: 1,
      total: 53,
    },
  },
  {
    payor_name: "Gulf Insurance Group - GIG",
    tot_status: {
      approved: 6,
      total: 6,
    },
  },
];

/**
 * Normalize a raw key from the API into our canonical key format.
 * - lowercases
 * - replace underscores with spaces
 * - normalize multiple spaces
 * - convert "partially approved" capitalization etc.
 */
function normalizeKey(rawKey) {
  if (!rawKey && rawKey !== 0) return rawKey;
  return (
    String(rawKey)
      .trim()
      .toLowerCase()
      .replace(/_/g, " ")
      .replace(/\s+/g, " ")
      // map common variations:
      .replace(/^error$/, "error") // already
      .replace(/^invalid$/, "invalid") // already
      .replace(/^no response$/, "no response")
      .replace(/^partially approved$/, "partially approved")
      .replace(/^partially approved$/i, "partially approved")
      .replace(/^not-required$/, "not-required")
      .replace(/^not required$/, "not-required")
      .replace(/^queued$/, "queued")
      .replace(/^queue$/, "queued")
  );
}

/**
 * Build a canonical tot_status object for a payor:
 * e.g. { "approved": 20, "no response": 1, "not-required": 13, ... }
 */
function canonicalizeTotStatus(rawTotStatus = {}) {
  const out = {};
  Object.entries(rawTotStatus || {}).forEach(([k, v]) => {
    const nk = normalizeKey(k);
    // Keep numeric values; non-numeric -> 0
    const num = Number(v || 0);
    if (nk) out[nk] = Number.isNaN(num) ? 0 : num;
  });
  return out;
}

const PreauthorizationVolume = () => {
  // use your redux data; fallback to SAMPLE_DATA for testing
  const { generalDashboardData } = useSelector(
    (state) => state.dashboard || {},
  );
  const rawPayors = generalDashboardData?.preauth_payor || SAMPLE_DATA;

  // canonicalized payors: keep payor_name and canonical tot_status
  const payors = rawPayors.map((p) => ({
    payor_name: p.payor_name || "Unknown",
    tot_status: canonicalizeTotStatus(p.tot_status || {}),
  }));

  // The order of statuses to show in the chart (stack order and legend order)
  // Keep this array in the exact order you want displayed (top of stack = last series).
  const statusOrder = [
    "error",
    "queued",
    "not-required",
    "cancelled",
    "prepared",
    "pended",
    "partially approved",
    "no response",
    "rejected",
    "approved",
  ];

  const categories = payors.map((p) => p.payor_name);

  const chart = useMemo(() => {
    // Build series for each status in statusOrder
    const series = statusOrder.map((key) => {
      const name = statusConfig[key]?.label || key;
      const data = payors.map((p) => Number(p.tot_status?.[key] || 0));
      return { name, data, key };
    });

    // Maximum for y axis (10% padding)
    const totals = payors.map((_, idx) =>
      series.reduce((sum, s) => sum + (s.data[idx] || 0), 0),
    );
    const maxTotal = totals.length ? Math.max(...totals) : 100;
    const yMax = Math.ceil(maxTotal * 2);

    // Apex options including a custom tooltip showing status + number
    const options = {
      chart: {
        type: "bar",
        stacked: true,
        toolbar: { show: false },
        zoom: { enabled: false },
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: "40%",
          barHeight: "40px", // Fixed bar height of 60px
        },
      },
      dataLabels: { enabled: false },
      xaxis: {
        categories,
        labels: {
          rotate: -30,
          style: { fontSize: "12px" },
          formatter: (val) => val,
        },
      },
      yaxis: {
        min: 0,
        max: yMax,
        tickAmount: 5,
        labels: { formatter: (v) => Math.floor(v) },
      },
      legend: {
        position: "bottom",
        horizontalAlign: "center",
        markers: { radius: 3, width: 12, height: 12 },
      },
      tooltip: {
        enabled: true,
        shared: true,
        intersect: false,
        // custom tooltip: shows the payor name, and then each status (colored square + label: value)
        custom: function ({ series, dataPointIndex, w }) {
          const category =
            (w.globals.labels && w.globals.labels[dataPointIndex]) || "";
          // series is array of arrays: series[i][dataPointIndex]
          const rowsHtml = statusOrder
            .map((key, idx) => {
              const value = (series[idx] && series[idx][dataPointIndex]) || 0;
              // only show statuses with non-zero value (optional). To show all, remove the `if` below.
              // if (value === 0) return "";
              const color = statusConfig[key]?.color || "#333";
              const label = statusConfig[key]?.label || key;
              // colored square + label + number
              return `<div style="display:flex; align-items:center; margin-bottom:6px; font-size:13px;">
                        <div style="width:10px; height:10px; background:${color}; border-radius:2px; margin-right:8px;"></div>
                        <div style="flex:1; color:#111;">${label}</div>
                        <div style="min-width:36px; text-align:right; font-weight:600;">${value}</div>
                      </div>`;
            })
            .filter(Boolean)
            .join("");

          return `
            <div style="padding:10px; font-family: Arial, Helvetica, sans-serif; font-size:13px; max-width:300px;">
              <div style="font-weight:700; margin-bottom:8px;">${category}</div>
              ${rowsHtml}
            </div>
          `;
        },
      },
      colors: statusOrder.map((k) => statusConfig[k]?.color || "#888"),
      grid: { borderColor: "#e9e9e9" },
    };

    // Build the series in the format Apex expects: { name, data }
    const apexSeries = series.map((s) => ({ name: s.name, data: s.data }));

    return { series: apexSeries, options };
  }, [payors.join?.(), categories.length]); // payors.join used just to add dependency; payors length + categories length sufficient in real apps

  return (
    <Col xs={12} className="mt-3">
      <Card className="h-100">
        <Card.Header as="h6" className="p-3 fs-9 text-primary">
          Preauthorization Volume by Payer
        </Card.Header>
        <Card.Body className="p-2">
          <ApexCharts
            options={chart.options}
            series={chart.series}
            type="bar"
            width="100%"
            height={Math.max(260, categories.length * 20)}
          />
        </Card.Body>
      </Card>
    </Col>
  );
};

export default PreauthorizationVolume;
